// The design patterns track (docs/areas/learn.md): the 22 classic patterns, as Alfie's own
// starting points. One-liners are in our own words; the pattern pages on Refactoring.Guru are
// linked, never copied. `where` is his own "where it likely shows up" column from the spec, verbatim: it becomes
// the start of the concept's my_notes, a hypothesis to correct as he sees the real thing.

export type PatternCategory = 'Creational' | 'Structural' | 'Behavioral'

export interface PatternSeed {
  /** the Refactoring.Guru page name: https://refactoring.guru/design-patterns/<slug> */
  slug: string
  name: string
  category: PatternCategory
  oneLiner: string
  where: string
}

export const PATTERN_CATEGORIES: PatternCategory[] = ['Creational', 'Structural', 'Behavioral']

export const patternUrl = (slug: string) => `https://refactoring.guru/design-patterns/${slug}`

export const PATTERNS: PatternSeed[] = [
  {
    slug: 'factory-method',
    name: 'Factory Method',
    category: 'Creational',
    oneLiner: 'A method decides which concrete type to build, so callers only ask for the interface.',
    where: 'Laravel Manager classes (createRedisDriver-style methods)',
  },
  {
    slug: 'abstract-factory',
    name: 'Abstract Factory',
    category: 'Creational',
    oneLiner: 'One factory hands out a whole family of matching objects that are meant to be used together.',
    where: 'Database connection factory: connection, grammar and processor per driver',
  },
  {
    slug: 'builder',
    name: 'Builder',
    category: 'Creational',
    oneLiner: 'Assemble a complex object step by step, then ask for the finished result.',
    where: 'Eloquent and the query builder',
  },
  {
    slug: 'prototype',
    name: 'Prototype',
    category: 'Creational',
    oneLiner: 'Make a new object by copying an existing one instead of building it from scratch.',
    where: 'Eloquent replicate()',
  },
  {
    slug: 'singleton',
    name: 'Singleton',
    category: 'Creational',
    oneLiner: 'Exactly one shared instance, reachable from anywhere.',
    where: '$app->singleton() bindings (container-managed, not the classic static form)',
  },
  {
    slug: 'adapter',
    name: 'Adapter',
    category: 'Structural',
    oneLiner: 'Wrap something so its interface looks like the one your code already expects.',
    where: 'Your ports-and-adapters layer; Flysystem storage adapters',
  },
  {
    slug: 'bridge',
    name: 'Bridge',
    category: 'Structural',
    oneLiner: 'Split an abstraction from its implementation so each can vary on its own.',
    where: 'Mailables sent over interchangeable mail transports',
  },
  {
    slug: 'composite',
    name: 'Composite',
    category: 'Structural',
    oneLiner: 'Treat a single item and a group of items the same way, as a tree.',
    where: 'Nested Blade components; any tree such as menus or org units',
  },
  {
    slug: 'decorator',
    name: 'Decorator',
    category: 'Structural',
    oneLiner: 'Wrap an object to add behaviour without changing it, keeping the same interface.',
    where: 'A caching repository wrapping the real one',
  },
  {
    slug: 'facade',
    name: 'Facade',
    category: 'Structural',
    oneLiner: 'Give a tangled subsystem one small, friendly front door.',
    where: 'Your service classes that hide several subsystems (not Laravel "Facades")',
  },
  {
    slug: 'flyweight',
    name: 'Flyweight',
    category: 'Structural',
    oneLiner: 'Share the common, unchanging part between many objects to save memory.',
    where: 'Rare in PHP; learn to recognise, little to apply',
  },
  {
    slug: 'proxy',
    name: 'Proxy',
    category: 'Structural',
    oneLiner: 'A stand-in that controls access to the real object: lazy, cached, checked or remote.',
    where: 'Laravel Facades: static proxies over container services',
  },
  {
    slug: 'chain-of-responsibility',
    name: 'Chain of Responsibility',
    category: 'Behavioral',
    oneLiner: 'Pass a request along a line of handlers until one deals with it.',
    where: 'HTTP middleware pipeline',
  },
  {
    slug: 'command',
    name: 'Command',
    category: 'Behavioral',
    oneLiner: 'Turn a request into an object, so it can be queued, logged, retried or undone.',
    where: 'Queued jobs and Artisan commands',
  },
  {
    slug: 'iterator',
    name: 'Iterator',
    category: 'Behavioral',
    oneLiner: 'Walk through a collection one item at a time without exposing how it is stored.',
    where: 'Collections, LazyCollection, cursor()',
  },
  {
    slug: 'mediator',
    name: 'Mediator',
    category: 'Behavioral',
    oneLiner: 'Have components talk through one coordinator instead of directly to each other.',
    where: 'The Kubernetes API server between controllers',
  },
  {
    slug: 'memento',
    name: 'Memento',
    category: 'Behavioral',
    oneLiner: "Capture an object's state as a snapshot so it can be restored later.",
    where: 'AlfieOS op log; Eloquent getOriginal() snapshots',
  },
  {
    slug: 'observer',
    name: 'Observer',
    category: 'Behavioral',
    oneLiner: 'Let things subscribe to an object and be told when it changes.',
    where: 'Eloquent observers, events and listeners; Kubernetes watches',
  },
  {
    slug: 'state',
    name: 'State',
    category: 'Behavioral',
    oneLiner: 'Let an object change its behaviour as its internal state changes, as if it changed class.',
    where: 'AlfieOS task states (inbox, next, waiting, done)',
  },
  {
    slug: 'strategy',
    name: 'Strategy',
    category: 'Behavioral',
    oneLiner: 'Make a family of interchangeable algorithms and pick one at runtime.',
    where: 'Per-tenant drivers; multi-provider model routing in the home lab',
  },
  {
    slug: 'template-method',
    name: 'Template Method',
    category: 'Behavioral',
    oneLiner: 'Fix the skeleton of an algorithm in one place and let steps be filled in by others.',
    where: 'FormRequest hooks (authorize, rules)',
  },
  {
    slug: 'visitor',
    name: 'Visitor',
    category: 'Behavioral',
    oneLiner: 'Add new operations to a set of types without editing those types.',
    where: 'PHP-Parser node visitors, as used by Rector and PHPStan',
  },
]

/** Patterns that are easy to confuse, or that often turn up together (slug pairs, one link each). */
export const RELATED: [string, string][] = [
  ['strategy', 'state'],
  ['decorator', 'proxy'],
  ['adapter', 'facade'],
  ['adapter', 'bridge'],
  ['observer', 'mediator'],
  ['command', 'memento'],
  ['factory-method', 'abstract-factory'],
  ['factory-method', 'template-method'],
  ['builder', 'abstract-factory'],
  ['composite', 'visitor'],
  ['iterator', 'visitor'],
  ['chain-of-responsibility', 'decorator'],
]
